import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

const normalizeId = (value) => String(value ?? '').split('/').pop();

/** Locate the next business date boundary, including 23/25-hour DST days. */
function delayUntilNextDate(now, timeZone) {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone, calendar: 'gregory', numberingSystem: 'latn',
    year: 'numeric', month: '2-digit', day: '2-digit',
  });
  const currentDate = formatter.format(now);
  let before = now.getTime();
  let after = before + 36 * 60 * 60 * 1000;
  // Search instants rather than adding 24 hours to a local midnight. This runs
  // only when arming one timer; it does not poll the clock or the storefront.
  while (after - before > 1) {
    const middle = Math.floor((before + after) / 2);
    if (formatter.format(new Date(middle)) === currentDate) before = middle;
    else after = middle;
  }
  return after - now.getTime() + 1;
}

/**
 * Enhances the existing shipping disclosure. Shopify's ProductSelectEvent and
 * native Product Form own selection and inventory; this component only reads
 * their committed state and the same returned section HTML. It never fetches a
 * second product response or keeps a separate set of selected option values.
 */
export class PdpDeliveryEstimate extends Component {
  #controller;
  #scope;
  #selectionGeneration = 0;
  #renderGeneration = 0;
  #selectionPending = false;
  #renderScheduled = false;
  #dateTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#clearDateTimer();
    this.#controller = new AbortController();
    this.#scope = this.closest('.shopify-section, dialog');
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, {
      signal: this.#controller.signal,
    });
    document.addEventListener('visibilitychange', this.#onPageVisibility, { signal: this.#controller.signal });
    window.addEventListener('pageshow', this.#onPageVisibility, { signal: this.#controller.signal });
    this.#scheduleRender();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#clearDateTimer();
    this.#scope = null;
    this.#selectionGeneration += 1;
    this.#renderGeneration += 1;
    this.#selectionPending = false;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#scheduleRender();
  }

  #restoreStatic() {
    this.#renderGeneration += 1;
    this.#clearDateTimer();
    const trigger = this.querySelector('[data-delivery-timing-trigger]');
    if (trigger) trigger.disabled = true;
    const popover = this.querySelector('[data-delivery-timing-popover]');
    if (popover?.matches(':popover-open')) popover.hidePopover();
    // Dates appear only after a complete, eligible calculation. The existing
    // shipping-policy link remains available when no dated estimate is possible.
    const row = this.querySelector('[data-delivery-dates]');
    if (row) row.hidden = true;
    const range = this.querySelector('[data-delivery-date-range]');
    if (range) range.textContent = '';
    this.removeAttribute('data-estimated-start');
    this.removeAttribute('data-estimated-end');
  }

  #clearDateTimer() {
    clearTimeout(this.#dateTimer);
    this.#dateTimer = undefined;
  }

  #onPageVisibility = () => {
    // Background pages hold no timer or stale visible estimate. Resume also
    // handles bfcache restores and invalidates any older pending module load.
    this.#restoreStatic();
    if (!document.hidden) this.#scheduleRender();
  };

  #scheduleRender() {
    if (this.#renderScheduled) return;
    this.#renderScheduled = true;
    queueMicrotask(() => {
      this.#renderScheduled = false;
      if (this.isConnected) void this.#render();
    });
  }

  #variantIsCommitted() {
    const variantId = normalizeId(this.dataset.variantId);
    if (!this.#scope) return false;
    // A general product estimate must not implicitly select a size. Once there
    // is an explicit variant, the native form still owns its committed state.
    if (!variantId) return this.dataset.mode === 'calendar-days';
    const form = Array.from(this.#scope.querySelectorAll('product-form-component')).find(
      (candidate) => candidate.dataset.productId === this.dataset.productId &&
        candidate.dataset.sectionId === this.dataset.sectionId && !candidate.closest('product-card'),
    );
    const input = form?.querySelector('input[name="id"]');
    return Boolean(input && !input.disabled && normalizeId(input.value) === variantId);
  }

  async #render() {
    this.#restoreStatic();
    if (document.hidden || this.#selectionPending || this.dataset.enabled !== 'true' || this.dataset.eligible !== 'true' || !this.#variantIsCommitted()) return;
    const generation = this.#renderGeneration;
    try {
      const calendarDays = this.dataset.mode === 'calendar-days';
      const calendars = calendarDays ? {} : {
        processingCalendar: JSON.parse(this.dataset.processingCalendar || 'null'),
        transitCalendar: JSON.parse(this.dataset.transitCalendar || 'null'),
      };
      // asset_url retains Shopify's versioned URL; a failed module import leaves
      // the server-rendered processing/transit ranges visible.
      const calculators = await import(this.dataset.calculatorUrl);
      if (!this.isConnected || document.hidden || this.#selectionPending || generation !== this.#renderGeneration || !this.#variantIsCommitted()) return;
      const now = new Date();
      // Calendar estimates follow the shopper's current local date when no
      // store timezone is configured, including their local midnight rollover.
      const timeZone = this.dataset.timeZone || (calendarDays ? Intl.DateTimeFormat().resolvedOptions().timeZone : '');
      const calculate = calendarDays ? calculators.estimateCalendarDayWindow : calculators.estimateDeliveryWindow;
      const estimate = calculate({
        now, timeZone, eligible: true,
        ...calendars,
      });
      if (!estimate) return;
      // The result is already a business calendar date. UTC formatting preserves
      // that date even for a shopper whose device is in a different timezone.
      const start = new Date(`${estimate.earliestDeliveryDate}T12:00:00Z`);
      const end = new Date(`${estimate.latestDeliveryDate}T12:00:00Z`);
      const crossesYear = estimate.orderDate.slice(0, 4) !== estimate.latestDeliveryDate.slice(0, 4);
      const formatter = new Intl.DateTimeFormat(this.dataset.locale || 'en', {
        month: 'short', day: 'numeric', ...(!calendarDays || crossesYear ? { year: 'numeric' } : {}), timeZone: 'UTC',
      });
      const range = this.querySelector('[data-delivery-date-range]');
      const row = this.querySelector('[data-delivery-dates]');
      if (!range || !row) return;
      const refreshDelay = delayUntilNextDate(now, timeZone);
      range.textContent = typeof formatter.formatRange === 'function'
        ? formatter.formatRange(start, end)
        : `${formatter.format(start)} – ${formatter.format(end)}`;
      this.dataset.estimatedStart = estimate.earliestDeliveryDate;
      this.dataset.estimatedEnd = estimate.latestDeliveryDate;
      row.hidden = false;
      const trigger = this.querySelector('[data-delivery-timing-trigger]');
      if (trigger) trigger.disabled = false;
      this.#dateTimer = setTimeout(() => {
        this.#dateTimer = undefined;
        this.#scheduleRender();
      }, refreshDelay);
    } catch {
      // Invalid configuration, missing Intl support, or a module failure is a
      // static fallback, never a customer-visible error or partial estimate.
    }
  }

  #onProductSelect = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (event.target.closest('.shopify-section, dialog') !== this.#scope) return;
    if (normalizeId(event.product?.id) !== normalizeId(this.dataset.productId)) return;
    const generation = ++this.#selectionGeneration;
    this.#selectionPending = true;
    this.#restoreStatic();
    this.dataset.eligible = 'false';
    this.dataset.variantId = '';
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || generation !== this.#selectionGeneration) return;
      this.#selectionPending = false;
      if (!detail?.html || !detail.resource?.id || detail.resource.available !== true) return;
      const responseProductId = normalizeId(detail.newProduct?.id ?? detail.productId);
      if (!detail.newProduct && responseProductId !== normalizeId(this.dataset.productId)) return;
      const freshElement = Array.from(detail.html.querySelectorAll('pdp-delivery-estimate')).find(
        (candidate) => candidate.dataset.blockId === this.dataset.blockId &&
          candidate.dataset.sectionId === this.dataset.sectionId &&
          normalizeId(candidate.dataset.productId) === responseProductId,
      );
      if (!freshElement || normalizeId(freshElement.dataset.variantId) !== normalizeId(detail.resource.id)) return;
      morph(this, freshElement, { ...MORPH_OPTIONS, childrenOnly: false });
      // Coalesces with Component.updatedCallback and runs after the native form's
      // promise handlers have had their turn to commit the selected variant.
      this.#scheduleRender();
    } catch {
      if (generation === this.#selectionGeneration) {
        this.#selectionPending = false;
        this.#restoreStatic();
      }
    }
  };
}

if (!customElements.get('pdp-delivery-estimate')) {
  customElements.define('pdp-delivery-estimate', PdpDeliveryEstimate);
}
